import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  Download, Send, Edit, User, MapPin, Phone, Mail, 
  Calendar, Building, Loader2, Printer, CreditCard,
  CheckCircle, Clock, AlertCircle, X, FileText, Save,
  Plus, Trash2, DollarSign
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function InvoiceView({ 
  open, 
  onOpenChange, 
  invoiceId,
  invoiceData: initialInvoiceData,
  onRefresh,
  editable = false,
  quoteData = null
}) {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(editable);
  const [customerPayments, setCustomerPayments] = useState([]);
  
  // Editable form state
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    due_days: 30,
    payment_terms: 'Net 30',
    custom_due_date: '',
    notes: '',
    items: [],
    tax_percent: 0,
    discount: 0,
    advance_paid: 0
  });
  
  // Actual payments from Payment section
  const [actualPaymentsTotal, setActualPaymentsTotal] = useState(0);

  // Always fetch fresh invoice data when dialog opens with an invoiceId
  useEffect(() => {
    if (open && invoiceId) {
      fetchInvoice();
    }
  }, [open, invoiceId]);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      setInvoice(response.data);
      initializeEditForm(response.data);
      if (response.data.customer_id) {
        fetchCustomerPayments(response.data.customer_id);
      }
    } catch (error) {
      console.error('InvoiceView: Error fetching invoice:', error);
      toast.error('Failed to load invoice');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerPayments = async (customerId, jobId = null) => {
    try {
      // Fetch payments made by this customer
      let url = `/payments?customer_id=${customerId}`;
      if (jobId) url += `&job_id=${jobId}`;
      const response = await axiosInstance.get(url);
      const payments = response.data || [];
      setCustomerPayments(payments);
      
      // Calculate total completed payments
      const total = payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      setActualPaymentsTotal(total);
    } catch (error) {
      console.log('No payments found');
      setCustomerPayments([]);
      setActualPaymentsTotal(0);
    }
  };

  const initializeEditForm = (inv) => {
    if (!inv) return;
    
    const isCustom = inv.payment_terms === 'Custom' || 
      (inv.payment_terms && !['Due upon receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60'].includes(inv.payment_terms));
    
    setEditForm({
      customer_name: inv.customer_name || '',
      customer_email: inv.customer_email || '',
      customer_phone: inv.customer_phone || '',
      customer_address: inv.customer_address || '',
      due_days: 30,
      payment_terms: isCustom ? 'Custom' : (inv.payment_terms || 'Net 30'),
      custom_due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
      notes: inv.notes || '',
      items: inv.items || [],
      tax_percent: inv.tax_percent || (inv.tax && inv.subtotal ? (inv.tax / inv.subtotal * 100) : 0),
      discount: inv.discount || 0,
      advance_paid: inv.advance_paid || 0
    });
  };

  const handleSendInvoice = async () => {
    setSending(true);
    try {
      await axiosInstance.post(`/invoices/${invoice.id}/send`);
      toast.success('Invoice sent successfully!');
      fetchInvoice();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handleSaveInvoice = async () => {
    setSaving(true);
    try {
      // Calculate totals
      const subtotal = editForm.items.reduce((sum, item) => sum + (item.total || 0), 0);
      const tax = subtotal * (editForm.tax_percent / 100);
      const total = subtotal + tax - editForm.discount;
      
      // Use advance_paid from form
      const advancePaid = parseFloat(editForm.advance_paid) || 0;
      const balanceDue = Math.max(0, total - advancePaid);
      
      // Calculate due date based on payment terms
      let dueDate = invoice.due_date;
      if (editForm.payment_terms === 'Custom' && editForm.custom_due_date) {
        dueDate = new Date(editForm.custom_due_date).toISOString();
      } else if (editForm.payment_terms === 'Due upon receipt') {
        dueDate = new Date().toISOString();
      } else {
        const days = parseInt(editForm.payment_terms.replace('Net ', '')) || 30;
        dueDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      }
      
      await axiosInstance.patch(`/invoices/${invoice.id}`, {
        customer_name: editForm.customer_name,
        customer_email: editForm.customer_email,
        customer_phone: editForm.customer_phone,
        customer_address: editForm.customer_address,
        payment_terms: editForm.payment_terms === 'Custom' ? `Custom: ${editForm.custom_due_date}` : editForm.payment_terms,
        due_date: dueDate,
        notes: editForm.notes,
        items: editForm.items,
        subtotal: subtotal,
        tax: tax,
        discount: editForm.discount,
        total_amount: total,
        advance_paid: advancePaid,
        amount_paid: advancePaid,
        balance_due: balanceDue
      });
      
      toast.success('Invoice saved!');
      setIsEditing(false);
      fetchInvoice();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axiosInstance.get(`/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error('PDF download error:', error);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate total for this item
    if (field === 'quantity' || field === 'unit_price') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
      const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
      newItems[index].total = qty * price;
    }
    
    setEditForm({ ...editForm, items: newItems });
  };

  const addLineItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, { name: '', description: '', quantity: 1, unit_price: 0, total: 0, unit: 'unit' }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, items: newItems });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileText, label: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Send, label: 'Sent' },
      payment_awaiting: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock, label: 'Awaiting Payment' },
      partially_paid: { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: CreditCard, label: 'Partially Paid' },
      paid: { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle, label: 'Paid' },
      overdue: { color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle, label: 'Overdue' },
    };
    return statusConfig[status] || statusConfig.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const items = isEditing ? editForm.items : (invoice?.items || []);
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const taxPercent = isEditing ? editForm.tax_percent : (invoice?.tax_percent || 0);
    const tax = isEditing ? (subtotal * taxPercent / 100) : (invoice?.tax || 0);
    const discount = isEditing ? parseFloat(editForm.discount) || 0 : (invoice?.discount || 0);
    const total = subtotal + tax - discount;
    
    // Get advance paid - from edit form when editing, otherwise from invoice
    let advancePaid = isEditing ? (parseFloat(editForm.advance_paid) || 0) : (invoice?.advance_paid || 0);
    
    // Get quote payment terms for display/reference only (not auto-applied)
    const quotePaymentTerms = invoice?.quote_payment_terms || quoteData?.payment_terms || null;
    
    // Total paid includes advance and any other payments
    const otherPayments = (invoice?.amount_paid || 0) - (invoice?.advance_paid || 0);
    const totalPaid = isEditing ? advancePaid : (advancePaid + Math.max(0, otherPayments));
    const balanceDue = Math.max(0, total - totalPaid);
    
    return { subtotal, tax, discount, total, advancePaid, otherPayments, totalPaid, balanceDue, quotePaymentTerms };
  };

  if (!open) return null;

  const statusInfo = getStatusBadge(invoice?.status);
  const StatusIcon = statusInfo.icon;
  const totals = calculateTotals();
  const displayItems = isEditing ? editForm.items : (invoice?.items || []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col p-0 print:max-w-none print:max-h-none print:overflow-visible">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
          </div>
        ) : invoice ? (
          <>
            {/* Action Bar - Hidden when printing */}
            <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between print:hidden">
              <div className="flex items-center gap-3">
                <Badge className={`${statusInfo.color} text-sm px-3 py-1 flex items-center gap-1`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {statusInfo.label}
                </Badge>
                {isEditing && (
                  <Badge className="bg-yellow-100 text-yellow-700 text-sm px-3 py-1">
                    <Edit className="w-3 h-3 mr-1" />
                    Editing
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        initializeEditForm(invoice);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveInvoice}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save Invoice
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handlePrint}
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleDownloadPdf}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSendInvoice}
                      disabled={sending}
                    >
                      {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      Send Invoice
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Invoice Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50 print:overflow-visible print:bg-white">
              <div className="max-w-3xl mx-auto my-6 print:my-0 print:max-w-none">
                {/* Invoice Paper */}
                <div className="bg-white shadow-lg rounded-lg print:shadow-none print:rounded-none">
                  
                  {/* Header Section */}
                  <div className="p-8 pb-6">
                    <div className="flex justify-between items-start">
                      {/* Company Logo and Info - Left */}
                      <div>
                        <img 
                          src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png" 
                          alt="Redefyne Logo" 
                          className="h-40 w-auto mb-3"
                        />
                        <p className="text-gray-600 text-sm">2301 S Stearman Dr, Chandler, AZ 85286</p>
                        <p className="text-gray-500 text-sm">+1 602-715-1313 | support@redefyneaz.com</p>
                      </div>
                      
                      {/* Invoice Title - Right */}
                      <div className="text-right">
                        <h2 className="text-3xl font-bold text-[#595959]">INVOICE</h2>
                        <p className="text-xl font-semibold text-gray-800 mt-1">#{invoice.invoice_number}</p>
                      </div>
                    </div>

                    {/* Invoice Meta */}
                    <div className="mt-6 flex justify-between">
                      <div className="flex gap-8">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Issued</p>
                          <p className="text-sm font-medium text-gray-800">{formatDate(invoice.issue_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Due</p>
                          <p className="text-sm font-medium text-gray-800">{formatDate(invoice.due_date)}</p>
                        </div>
                      </div>
                      {invoice.quote_id && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Reference</p>
                          <p className="text-sm text-gray-600">Quote #{invoice.quote_id?.slice(0, 8).toUpperCase()}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-200 mx-8" />

                  {/* Customer & Billing Section */}
                  <div className="p-8 py-6">
                    <div className="grid grid-cols-2 gap-8">
                      {/* Bill To */}
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <User className="w-3 h-3" /> Bill To
                        </p>
                        {isEditing ? (
                          <div className="space-y-2">
                            <Input
                              value={editForm.customer_name}
                              onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                              placeholder="Customer Name"
                              className="font-semibold"
                            />
                            <Input
                              value={editForm.customer_address}
                              onChange={(e) => setEditForm({...editForm, customer_address: e.target.value})}
                              placeholder="Address"
                            />
                            <Input
                              value={editForm.customer_phone}
                              onChange={(e) => setEditForm({...editForm, customer_phone: e.target.value})}
                              placeholder="Phone"
                            />
                            <Input
                              value={editForm.customer_email}
                              onChange={(e) => setEditForm({...editForm, customer_email: e.target.value})}
                              placeholder="Email"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="font-semibold text-gray-900 text-lg">{invoice.customer_name}</p>
                            {invoice.customer_address && (
                              <p className="text-gray-600 text-sm mt-1 flex items-start gap-1">
                                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <span>{invoice.customer_address}</span>
                              </p>
                            )}
                            {invoice.customer_phone && (
                              <p className="text-gray-600 text-sm mt-1 flex items-center gap-1">
                                <Phone className="w-3.5 h-3.5" />
                                {invoice.customer_phone}
                              </p>
                            )}
                            {invoice.customer_email && (
                              <p className="text-gray-600 text-sm mt-1 flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {invoice.customer_email}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Payment Terms */}
                      <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Payment Terms</p>
                        {isEditing ? (
                          <div className="space-y-2">
                            <Select 
                              value={editForm.payment_terms} 
                              onValueChange={(value) => setEditForm({...editForm, payment_terms: value})}
                            >
                              <SelectTrigger className="w-48 ml-auto">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Due upon receipt">Due upon receipt</SelectItem>
                                <SelectItem value="Net 7">Net 7</SelectItem>
                                <SelectItem value="Net 15">Net 15</SelectItem>
                                <SelectItem value="Net 30">Net 30</SelectItem>
                                <SelectItem value="Net 45">Net 45</SelectItem>
                                <SelectItem value="Net 60">Net 60</SelectItem>
                                <SelectItem value="Custom">Custom Due Date</SelectItem>
                              </SelectContent>
                            </Select>
                            {editForm.payment_terms === 'Custom' && (
                              <div className="flex items-center gap-2 justify-end mt-2">
                                <Label className="text-xs text-gray-500">Due Date:</Label>
                                <Input
                                  type="date"
                                  value={editForm.custom_due_date}
                                  onChange={(e) => setEditForm({...editForm, custom_due_date: e.target.value})}
                                  className="w-40"
                                  min={new Date().toISOString().split('T')[0]}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-700 text-sm">{invoice.payment_terms || 'Due upon receipt'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <div className="px-8">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-y border-gray-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Product / Service</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">Qty</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-28">Unit Price</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide w-28">Total</th>
                          {isEditing && <th className="w-12"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {displayItems.map((item, index) => (
                          <tr key={item.id || index} className="hover:bg-gray-50">
                            <td className="py-4 px-4">
                              {isEditing ? (
                                <div className="space-y-1">
                                  <Input
                                    value={item.name || ''}
                                    onChange={(e) => updateLineItem(index, 'name', e.target.value)}
                                    placeholder="Item name"
                                    className="font-medium"
                                  />
                                  <Input
                                    value={item.description || ''}
                                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                    placeholder="Description (optional)"
                                    className="text-sm"
                                  />
                                </div>
                              ) : (
                                <>
                                  <p className="font-medium text-gray-900">{item.name}</p>
                                  {item.description && (
                                    <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{item.description}</p>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="py-4 px-4 text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={item.quantity || ''}
                                  onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                  className="w-16 text-center"
                                  min="0"
                                />
                              ) : (
                                <span className="text-gray-700">{item.quantity} {item.unit || ''}</span>
                              )}
                            </td>
                            <td className="py-4 px-4 text-right">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={item.unit_price || ''}
                                  onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                  className="w-24 text-right"
                                  min="0"
                                  step="0.01"
                                />
                              ) : (
                                <span className="text-gray-700">${Number(item.unit_price || 0).toFixed(2)}</span>
                              )}
                            </td>
                            <td className="py-4 px-4 text-right font-medium text-gray-900">
                              ${(parseFloat(item.total) || 0).toFixed(2)}
                            </td>
                            {isEditing && (
                              <td className="py-4 px-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeLineItem(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {isEditing && (
                      <div className="py-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={addLineItem}
                          className="w-full border-dashed"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Line Item
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Totals Section */}
                  <div className="px-8 py-6">
                    <div className="flex justify-end">
                      <div className="w-80">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span>
                          </div>
                          
                          {isEditing ? (
                            <>
                              <div className="flex justify-between text-sm items-center">
                                <span className="text-gray-600">Tax %</span>
                                <Input
                                  type="number"
                                  value={editForm.tax_percent}
                                  onChange={(e) => setEditForm({...editForm, tax_percent: parseFloat(e.target.value) || 0})}
                                  className="w-20 text-right"
                                  min="0"
                                  step="0.1"
                                />
                              </div>
                              <div className="flex justify-between text-sm items-center">
                                <span className="text-gray-600">Discount</span>
                                <Input
                                  type="number"
                                  value={editForm.discount}
                                  onChange={(e) => setEditForm({...editForm, discount: parseFloat(e.target.value) || 0})}
                                  className="w-24 text-right"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              {totals.discount > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Discount</span>
                                  <span className="text-red-600">-${totals.discount.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Tax</span>
                                <span className="text-gray-900">${totals.tax.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                          
                          <div className="h-px bg-gray-200 my-2" />
                          
                          <div className="flex justify-between text-lg font-bold">
                            <span className="text-gray-900">Total</span>
                            <span className="text-[#595959]">${totals.total.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Advance Payment Management Section */}
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <CreditCard className="w-4 h-4 text-[#595959]" />
                            Payment Management
                          </div>
                          
                          {/* Actual Payments from Payment Section */}
                          {actualPaymentsTotal > 0 && (
                            <div className="p-2 bg-blue-50 rounded border border-blue-200">
                              <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Payments Received (from Payments)</span>
                                <span className="text-blue-700 font-medium">${actualPaymentsTotal.toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                          
                          {isEditing ? (
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm items-center">
                                <span className="text-gray-600">Advance/Prepaid Amount</span>
                                <Input
                                  type="number"
                                  value={editForm.advance_paid}
                                  onChange={(e) => setEditForm({...editForm, advance_paid: parseFloat(e.target.value) || 0})}
                                  className="w-28 text-right"
                                  min="0"
                                  step="0.01"
                                  data-testid="advance-paid-input"
                                />
                              </div>
                              {actualPaymentsTotal > 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setEditForm({...editForm, advance_paid: actualPaymentsTotal})}
                                  className="text-xs w-full"
                                >
                                  Use Actual Payments (${actualPaymentsTotal.toFixed(2)})
                                </Button>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Advance Paid Display */}
                              {totals.advancePaid > 0 && (
                                <div className="flex justify-between text-sm bg-green-50 p-2 rounded">
                                  <span className="text-green-700 font-medium">
                                    <DollarSign className="w-3 h-3 inline mr-1" />
                                    Advance Paid
                                  </span>
                                  <span className="text-green-600 font-medium">-${totals.advancePaid.toFixed(2)}</span>
                                </div>
                              )}
                            </>
                          )}
                          
                          {/* Other Payments (if any beyond advance) */}
                          {!isEditing && totals.otherPayments > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Other Payments</span>
                              <span className="text-green-600">-${totals.otherPayments.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* Total Paid Summary */}
                          {totals.totalPaid > 0 && (
                            <div className="flex justify-between text-sm font-medium border-t border-gray-100 pt-2">
                              <span className="text-gray-700">Total Paid</span>
                              <span className="text-green-600">-${totals.totalPaid.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* Balance Due */}
                          <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                            <span className="font-bold text-gray-800">Balance Due</span>
                            <span className={`font-bold text-xl ${totals.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ${totals.balanceDue.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Show Quote Payment Terms breakdown if available */}
                          {totals.quotePaymentTerms && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs">
                              <p className="text-gray-500 uppercase font-semibold mb-2">Quote Payment Schedule</p>
                              <div className="space-y-1 text-gray-600">
                                <div className="flex justify-between">
                                  <span>Advance ({totals.quotePaymentTerms.advance_percent || 0}%)</span>
                                  <span className={totals.advancePaid > 0 ? 'text-green-600' : ''}>
                                    ${(totals.quotePaymentTerms.advance_amount || 0).toFixed(2)}
                                    {totals.advancePaid > 0 && ' ✓'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Interim ({totals.quotePaymentTerms.interim_percent || 0}%)</span>
                                  <span>${(totals.quotePaymentTerms.interim_amount || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Final ({totals.quotePaymentTerms.final_percent || 0}%)</span>
                                  <span>${(totals.quotePaymentTerms.final_amount || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes & Footer */}
                  <div className="px-8 pb-8">
                    {isEditing ? (
                      <div className="mb-4">
                        <Label className="text-xs text-gray-500 uppercase tracking-wide">Notes</Label>
                        <Textarea
                          value={editForm.notes}
                          onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                          placeholder="Add notes for this invoice..."
                          rows={3}
                          className="mt-2"
                        />
                      </div>
                    ) : invoice.notes && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Notes</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
                      </div>
                    )}
                    
                    {/* Thank You Message */}
                    <div className="text-center pt-6 border-t border-gray-100">
                      <p className="text-gray-600 text-sm">
                        Thank you for your business. Please contact us with any questions regarding this invoice.
                      </p>
                      <p className="text-gray-400 text-xs mt-2">
                        2301 S Stearman Dr, Chandler, AZ 85286 • +1 602-715-1313 • support@redefyneaz.com
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-96">
            <p className="text-gray-500">Invoice not found</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
